/**
 * Detail synopsis component.
 */
import DetailContext from '@shared/components/utils/context/detailContext';
import DetailSynopsisArrows from './detailSynopsisArrows';
import DetailSynopsisContainer from '@components/atoms/container/detailSynopsisContainer';
import DetailSynopsisText from '@components/atoms/text/detailSynopsisText';
import Focus from '@shared/types/enums/focus';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Constants.
 *
 * @constant {number} TEXT_HEIGHT - Height of the text in the synopsis.
 */
const TEXT_HEIGHT = 120;

/**
 * Renders the detail synopsis component. Handles navigation through the synopsis text.
 */
export default function DetailSynopsis() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------
  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const focus = useAppSelector(selectFocus);

  // Context
  const { focusable, metadata } = useContext(DetailContext);

  // State
  const [height, setHeight] = useState(0);
  const [offset, setOffset] = useState(0);

  // Refs
  const containerRef = useRef(null);
  const overflownRef = useRef(false);
  const textRef = useRef<HTMLDivElement>(null);
  const synopsisRef = useRef(metadata?.synopsis);

  // Memoized values
  const { isAhead, isOverflown, synopsis } = useMemo(() => {
    const dotsLength = height ? Math.ceil(height / TEXT_HEIGHT) : 0;
    const dotsIndex = Math.floor(Math.abs(offset) / TEXT_HEIGHT);
    const isAhead = offset !== 0;
    const isOverflown = textRef.current
      ? Math.abs(offset) + TEXT_HEIGHT < height
      : false;
    return {
      dotsLength,
      dotsIndex,
      isAhead,
      isOverflown,
      synopsis: metadata?.synopsis,
    };
  }, [height, metadata, offset]);

  overflownRef.current = isOverflown;
  synopsisRef.current = metadata?.synopsis;

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    setHeight(textRef.current?.clientHeight || 0);
  }, [textRef.current]);

  useEffect(() => {
    focusable.current.synopsis = isOverflown || isAhead;
  }, [isOverflown, isAhead, synopsis]);

  // ------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------

  const navigateLeft = useCallback(() => {
    setOffset((offset) => {
      if (textRef.current) {
        const newOffset = Math.min(offset + TEXT_HEIGHT, 0);
        textRef.current.style.transform = `translateY(${newOffset}px)`;
        if (newOffset !== offset) return newOffset;
      }
      dispatch(setFocus(Focus.MENU));
      return offset;
    });
  }, []);

  const navigateRight = useCallback(() => {
    setOffset((offset) => {
      if (textRef.current) {
        let newOffset = offset - TEXT_HEIGHT;
        if (newOffset <= -textRef.current.clientHeight) {
          newOffset = offset;
        }
        textRef.current.style.transform = `translateY(${newOffset}px)`;
        return newOffset;
      }
      return offset;
    });
  }, []);

  const onMouseEnter = useCallback(() => {
    if (
      synopsisRef.current?.length &&
      synopsisRef.current?.length > 1 &&
      overflownRef.current
    ) {
      dispatch(setFocus(Focus.DETAIL_SYNOPSIS));
    }
  }, []);

  // ------------------------------------------------------------
  // Keyboard handlers
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.DETAIL_SYNOPSIS,
    keyDownHandler: () => null,
    keyUpHandler: (key) => {
      if (!textRef.current) return;
      if (!synopsisRef?.current?.length) {
        return;
      }
      switch (key) {
        case 'LEFT':
          navigateLeft();
          break;
        case 'RIGHT':
          navigateRight();
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return synopsis?.length ? (
    <DetailSynopsisContainer
      ref={containerRef}
      id={'synopsisContainer'}
      onMouseEnter={onMouseEnter}
    >
      <DetailSynopsisText ref={textRef} id={'synopsisText'}>
        {synopsis}
      </DetailSynopsisText>
      <DetailSynopsisArrows
        display={{ left: isAhead, right: isOverflown }}
        focused={focus === Focus.DETAIL_SYNOPSIS}
        navigate={{ left: navigateLeft, right: navigateRight }}
      />
    </DetailSynopsisContainer>
  ) : null;
}
